/**
 * ContractorEarningsCard (P8 IA cleanup) — collapsed earnings overlay.
 *
 * Strict reuse of computeContractorEarnings(). payoutAmountSnapshot only,
 * never customer revenue. Pending offers excluded from earnings (locked).
 */

import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, DollarSign } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import {
  computeContractorEarnings,
  filterContractorJobs,
} from "@/data/contractor/commandCenter";

export function ContractorEarningsCard() {
  const { jobs, marketplaceOffers } = useAppData();
  const { id: contractorId, matchKey } = useCurrentContractor();
  const [open, setOpen] = useState(false);

  const myJobs = useMemo(
    () => filterContractorJobs(jobs, contractorId, matchKey),
    [jobs, contractorId, matchKey],
  );
  const earnings = useMemo(
    () => computeContractorEarnings(myJobs, marketplaceOffers),
    [myJobs, marketplaceOffers],
  );

  return (
    <Card data-testid="earnings-card" data-state={open ? "open" : "collapsed"}>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="earnings-toggle"
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">
            <DollarSign className="h-4 w-4" /> Earnings
          </span>
          <span className="flex items-center gap-2">
            <span className="text-xs font-semibold">
              ${earnings.weekRealized.toLocaleString()}
              <span className="text-[10px] text-muted-foreground font-normal ml-1">
                this week
              </span>
            </span>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </span>
        </button>
        {open && (
          <div
            data-testid="earnings-overlay"
            className="grid grid-cols-3 gap-2 border-t border-border/50 px-4 py-3"
          >
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Today
              </p>
              <p
                data-testid="earnings-today-realized"
                className="text-sm font-bold mt-0.5"
              >
                ${earnings.todayRealized.toLocaleString()}
              </p>
              <p className="text-[9px] text-muted-foreground">Realized</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                This Week
              </p>
              <p
                data-testid="earnings-week-realized"
                className="text-sm font-bold mt-0.5"
              >
                ${earnings.weekRealized.toLocaleString()}
              </p>
              <p className="text-[9px] text-muted-foreground">Realized</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Upcoming
              </p>
              <p
                data-testid="earnings-tentative"
                className="text-sm font-bold mt-0.5"
              >
                ${earnings.upcomingTentative.toLocaleString()}
              </p>
              <p className="text-[9px] text-muted-foreground">
                Tentative — pending completion
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ContractorEarningsCard;
